<template>
    <div class="main-problems-report">
        <div class="card shadow-sm border-0">

            <HeaderFilter
                title="Principais Avarias Reportadas"
                @apply-filters="loadData"
            />

            <div class="card-body">
                <!-- Loading -->
                <div
                    v-if="loadingData"
                    class="text-center py-4"
                >
                    <i class="fas fa-spinner fa-spin fa-2x text-danger"></i>
                    <p class="mt-2">Analisando problemas...</p>
                </div>

                <!-- Conteúdo Principal -->
                <div v-else>
                    <!-- Alerta de Status -->
                    <div class="status-alert mb-4">
                        <div class="alert alert-info">
                            <div class="alert-content">
                                <div class="alert-text">

                                    <div class="alert-details">
                                        {{ getTotalProblems() }} problemas identificados • {{ getTotalOkItems() }} itens
                                        funcionando corretamente
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Top Problemas -->
                    <div
                        class="top-problems mb-4"
                        v-if="hasProblems"
                    >
                        <h6 class="section-title">
                            <i class="fas fa-list-ol mr-2"></i>
                            Problemas Mais Reportados
                        </h6>
                        <div class="problems-list">
                            <div
                                v-for="(problem, index) in getTopProblems()"
                                :key="`problem-rank-${index}`"
                                class="problem-item"
                                :class="getProblemSeverityClass(problem, index)"
                            >
                                <div class="problem-rank">{{ index + 1 }}</div>
                                <div class="problem-content">
                                    <div class="problem-header">
                                        <div class="problem-title">{{ problem.label }}</div>
                                        <div class="problem-severity">
                                            <span
                                                class="severity-badge"
                                                :class="getSeverityClass(index)"
                                            >
                                                {{ getSeverityLabel(index) }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="problem-stats">
                                        <div class="problem-count">
                                            <i class="fas fa-bug mr-1"></i>
                                            {{ problem.count }} ocorrências
                                        </div>
                                        <div class="problem-percentage">
                                            {{ getPercentage(problem.count) }}% dos problemas
                                        </div>
                                    </div>
                                </div>
                                <div class="problem-indicator">
                                    <div class="indicator-bar">
                                        <div
                                            class="indicator-fill"
                                            :style="`width: ${getPercentage(problem.count)}%`"
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Gráfico de Problemas -->
                    <div
                        class="chart-container mb-4"
                        v-if="hasData"
                    >
                        <div class="chart-header">
                            <h6 class="chart-title">
                                <i class="fas fa-chart-bar mr-2"></i>
                                Distribuição por Período (Problemas + Itens OK)
                            </h6>
                        </div>
                        <ChartBar
                            v-if="renderChart && chartData.labels"
                            :chart-data="chartData"
                            :options="chartOptions"
                            :height="300"
                        />
                    </div>

                    <!-- Tabela Detalhada -->
                    <div
                        class="problems-table"
                        v-if="hasData"
                    >
                        <div class="table-responsive">
                            <table class="table table-hover">
                                <thead class="bg-light">
                                <tr>
                                    <th>Item/Problema</th>
                                    <th
                                        v-for="column in getTableColumns()"
                                        :key="`column-1-${column}`"
                                        class="text-center"
                                    >
                                        {{ column }}
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <!-- Linha especial para itens OK -->
                                <tr
                                    v-if="getOkDataset()"
                                    class="ok-row"
                                >
                                    <td>
                                        <div class="problem-info">
                                            <div class="problem-name">
                                                <i class="fas fa-check-circle text-success mr-2"></i>
                                                {{ getOkDataset().label }}
                                            </div>
                                            <div class="problem-meta">
                                                <span class="impact-badge ok-status">
                                                    Funcionando Corretamente
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td
                                        v-for="(value, index) in getOkDataset().data"
                                        :key="`badge-${index}`"
                                        class="text-center"
                                    >
                                        <span class="badge badge-success">{{ value }}</span>
                                    </td>
                                </tr>
                                <!-- Linhas dos problemas -->
                                <tr
                                    v-for="(problem, index) in getTableProblems()"
                                    :key="`tr-1-${index}`"
                                >
                                    <td>
                                        <div class="problem-info">
                                            <div class="problem-name">
                                                <i class="fas fa-exclamation-triangle text-danger mr-2"></i>
                                                {{ problem.label }}
                                            </div>
                                            <div class="problem-meta">
                                                <span
                                                    class="impact-badge"
                                                    :class="getImpactClass(problem.count)"
                                                >
                                                    {{ getImpactLabel(problem.count) }}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td
                                        v-for="(value, index) in problem.data"
                                        :key="`badge-danger-${index}`"
                                        class="text-center"
                                    >
                                        <span class="badge badge-danger">{{ value }}</span>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Estado Vazio -->
                    <div
                        v-if="!hasProblems && hasData"
                        class="empty-state"
                    >
                        <div class="empty-icon">
                            <i class="fas fa-smile fa-3x text-success"></i>
                        </div>
                        <div class="empty-title">Nenhum Problema Identificado</div>
                        <div class="empty-subtitle">
                            Excelente! Todos os {{ getTotalOkItems() }} itens analisados estão funcionando corretamente.
                        </div>
                    </div>

                    <!-- Estado completamente vazio -->
                    <div
                        v-if="!hasData"
                        class="empty-state"
                    >
                        <div class="empty-icon">
                            <i class="fas fa-search fa-3x text-muted"></i>
                        </div>
                        <div class="empty-title">Nenhum Dado Encontrado</div>
                        <div class="empty-subtitle">
                            Não foram encontrados dados para o período selecionado.
                        </div>
                    </div>

                    <!-- Análise de Tendências -->
                    <!--                    <div class="trend-analysis mt-4" v-if="hasProblems">-->
                    <!--                        <div class="row">-->
                    <!--                            <div class="col-md-6">-->
                    <!--                                <div class="analysis-card">-->
                    <!--                                    <h6 class="analysis-title">-->
                    <!--                                        <i class="fas fa-trendingdown mr-2"></i>-->
                    <!--                                        Análise de Tendências-->
                    <!--                                    </h6>-->
                    <!--                                    <div class="analysis-content">-->
                    <!--                                        <div class="trend-item">-->
                    <!--                                            <div class="trend-label">Problema Mais Frequente:</div>-->
                    <!--                                            <div class="trend-value text-danger">{{ mostFrequentProblem }}</div>-->
                    <!--                                        </div>-->
                    <!--                                        <div class="trend-item">-->
                    <!--                                            <div class="trend-label">Concentração Top 3:</div>-->
                    <!--                                            <div class="trend-value">{{ topProblemsConcentration }}%</div>-->
                    <!--                                        </div>-->
                    <!--                                        <div class="trend-item">-->
                    <!--                                            <div class="trend-label">Diversidade de Problemas:</div>-->
                    <!--                                            <div class="trend-value">{{ problemsDiversity }}</div>-->
                    <!--                                        </div>-->
                    <!--                                    </div>-->
                    <!--                                </div>-->
                    <!--                            </div>-->
                    <!--                            <div class="col-md-6">-->
                    <!--                                <div class="analysis-card">-->
                    <!--                                    <h6 class="analysis-title">-->
                    <!--                                        <i class="fas fa-tools mr-2"></i>-->
                    <!--                                        Recomendações-->
                    <!--                                    </h6>-->
                    <!--                                    <div class="analysis-content">-->
                    <!--                                        <div class="recommendation-item">-->
                    <!--                                            <div class="recommendation-icon">-->
                    <!--                                                <i class="fas fa-lightbulb"></i>-->
                    <!--                                            </div>-->
                    <!--                                            <div class="recommendation-text">-->
                    <!--                                                {{ getMainRecommendation() }}-->
                    <!--                                            </div>-->
                    <!--                                        </div>-->
                    <!--                                        <div class="recommendation-item">-->
                    <!--                                            <div class="recommendation-icon">-->
                    <!--                                                <i class="fas fa-wrench"></i>-->
                    <!--                                            </div>-->
                    <!--                                            <div class="recommendation-text">-->
                    <!--                                                {{ getSecondaryRecommendation() }}-->
                    <!--                                            </div>-->
                    <!--                                        </div>-->
                    <!--                                    </div>-->
                    <!--                                </div>-->
                    <!--                            </div>-->
                    <!--                        </div>-->
                    <!--                    </div>-->
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ChartBar from "@/components/Utils/ChartBar.js";
import {getMessage} from "../../composables/message";
import HeaderFilter from "./HeaderFilter.vue";

export default {
    name: "MainProblemsReport", // nome do componente

    components: {
        HeaderFilter,
        ChartBar, // componente de gráfico de barras
    },

    props: {
        globalFilters: {
            type: Object,
            default: () => ({}) // filtros globais passados pelo componente pai
        },
        loading: {
            type: Boolean,
            default: false // estado de carregamento externo
        }
    },

    data() {
        return {
            loadingData: false, // controla carregamento dos dados
            renderChart: false, // controla renderização do gráfico

            // dashData - dados do dashboard
            dashData: {
                graphics: {
                    problemsperiodPerUser: {
                        labels: [], // períodos
                        datasets: [] // dados dos problemas
                    }
                }
            },

            // chartOptions - opções de configuração do gráfico
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'top',
                        labels: {
                            usePointStyle: true,
                            padding: 20
                        }
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: 'white',
                        bodyColor: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        borderWidth: 1,
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        grid: {
                            color: 'rgba(0, 0, 0, 0.1)'
                        },
                        ticks: {
                            stepSize: 1
                        }
                    },
                    x: {
                        grid: {
                            display: false
                        }
                    }
                }
            },
        };
    },

    computed: {
        /**
         * Verifica se há dados / Check if there's data
         */
        hasData() {
            return this.dashData.graphics.problemsperiodPerUser.datasets.length > 0;
        },

        /**
         * Filtra apenas datasets que são problemas (exclui "OK") / Filter only problem datasets (exclude "OK")
         */
        problemDatasets() {
            if (!this.hasData) return [];
            return this.dashData.graphics.problemsperiodPerUser.datasets.filter(
                dataset => dataset.label.toUpperCase() !== 'OK'
            );
        },

        /**
         * Verifica se há problemas / Check if there are problems
         */
        hasProblems() {
            return this.problemDatasets.length > 0 && this.totalProblems > 0;
        },

        /**
         * Dados do gráfico / Chart data
         */
        chartData() {
            return this.dashData.graphics.problemsperiodPerUser;
        },

        /**
         * Total de problemas (excluindo OK) / Total problems (excluding OK)
         */
        totalProblems() {
            if (!this.hasData) return 0;
            return this.problemDatasets.reduce((sum, dataset) => {
                return sum + dataset.data.reduce((dataSum, value) => dataSum + value, 0);
            }, 0);
        },

        /**
         * Total de itens OK
         */
        totalOkItems() {
            if (!this.hasData) return 0;
            const okDataset = this.dashData.graphics.problemsperiodPerUser.datasets.find(
                dataset => dataset.label.toUpperCase() === 'OK'
            );
            if (!okDataset) return 0;
            return okDataset.data.reduce((sum, value) => sum + value, 0);
        },
    },

    watch: {
        // Observa mudanças nos filtros globais / Watch global filters changes
        globalFilters: {
            handler() {
                this.loadData();
            },
            deep: true
        },

        // Observa mudanças no estado de loading externo / Watch external loading state
        loading(newVal) {
            if (!newVal) {
                this.loadData();
            }
        }
    },

    mounted() {
        this.loadData(); // carrega dados iniciais
    },

    methods: {
        /**
         * Carrega dados do relatório / Load report data
         */
        async loadData(params) {
            this.loadingData = true;
            this.renderChart = false;

            try {
                const api = this.$store.state.api + "main_problems";

                const response = await this.$http.get(api, {params});

                this.dashData = response.data || {graphics: {problemsperiodPerUser: {labels: [], datasets: []}}};

                // Aguarda um pouco antes de renderizar o gráfico
                setTimeout(() => {
                    this.renderChart = true;
                }, 100);

                // Emite evento para o componente pai / Emit event to parent component
                this.$emit('data-updated', 'main-problems', response.data);

            } catch (error) {
                console.error('Erro ao carregar principais problemas:', error);
                const message = error.response.data.errors
                    ? getMessage(error.response.data.errors)
                    : error.response.data || 'Erro ao carregar dados';
                this.$message(null, message, "error");
            } finally {
                this.loadingData = false;
            }
        },

        /**
         * Retorna total de problemas / Return total problems
         */
        getTotalProblems() {
            return this.totalProblems;
        },

        /**
         * Retorna total de itens OK / Return total OK items
         */
        getTotalOkItems() {
            return this.totalOkItems;
        },

        /**
         * Retorna o dataset "OK" / Return "OK" dataset
         */
        getOkDataset() {
            if (!this.hasData) return null;
            return this.dashData.graphics.problemsperiodPerUser.datasets.find(
                dataset => dataset.label.toUpperCase() === 'OK'
            );
        },

        /**
         * Retorna top problemas / Return top problems
         */
        getTopProblems() {
            if (!this.hasProblems) return [];

            return this.problemDatasets
                .map(dataset => ({
                    label: dataset.label,
                    count: dataset.data.reduce((sum, val) => sum + val, 0)
                }))
                .filter(problem => problem.count > 0) // só problemas com ocorrências
                .sort((a, b) => b.count - a.count)
                .slice(0, 5);
        },

        /**
         * Retorna colunas da tabela / Return table columns
         */
        getTableColumns() {
            if (!this.hasData) return [];
            return this.dashData.graphics.problemsperiodPerUser.labels || [];
        },

        /**
         * Retorna problemas para tabela / Return problems for table
         */
        getTableProblems() {
            if (!this.hasProblems) return [];

            return this.problemDatasets.map(dataset => ({
                label: dataset.label,
                data: dataset.data,
                count: dataset.data.reduce((sum, val) => sum + val, 0)
            }));
        },

        /**
         * Calcula percentual / Calculate percentage
         */
        getPercentage(value) {
            if (this.totalProblems === 0) return 0;
            return Math.round((value / this.totalProblems) * 100);
        },

        /**
         * Retorna classe de severidade do problema / Return problem severity class
         */
        getProblemSeverityClass(problem, index) {
            if (index === 0) return 'critical-problem';
            if (index === 1) return 'high-problem';
            if (index === 2) return 'medium-problem';
            return 'low-problem';
        },

        /**
         * Retorna classe de severidade / Return severity class
         */
        getSeverityClass(index) {
            if (index === 0) return 'critical';
            if (index === 1) return 'high';
            if (index === 2) return 'medium';
            return 'low';
        },

        /**
         * Retorna label de severidade / Return severity label
         */
        getSeverityLabel(index) {
            if (index === 0) return 'Crítico';
            if (index === 1) return 'Alto';
            if (index === 2) return 'Médio';
            return 'Baixo';
        },

        /**
         * Retorna classe de impacto / Return impact class
         */
        getImpactClass(count) {
            const percentage = this.getPercentage(count);
            if (percentage >= 30) return 'high-impact';
            if (percentage >= 15) return 'medium-impact';
            return 'low-impact';
        },

        /**
         * Retorna label de impacto / Return impact label
         */
        getImpactLabel(count) {
            const percentage = this.getPercentage(count);
            if (percentage >= 30) return 'Alto Impacto';
            if (percentage >= 15) return 'Médio Impacto';
            return 'Baixo Impacto';
        },
    },
};
</script>

<style scoped>
/* Estilo principal do relatório */
.main-problems-report .card {
    border-radius: 12px;
    overflow: hidden;
}

.bg-gradient-danger {
    background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%);
}

.text-danger {
    color: #dc3545;
}

.btn-danger {
    background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%);
    border: none;
    color: white;
}

.btn-danger:hover {
    background: linear-gradient(135deg, #bd2130 0%, #dc3545 100%);
    color: white;
}

/* Alerta de status */
.status-alert {
    margin-bottom: 1.5rem;
}

.alert {
    border: none;
    border-radius: 12px;
    padding: 1.5rem;
}

.alert-content {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.alert-icon {
    font-size: 2rem;
}

.alert-text {
    flex: 1;
}

.alert-details {
    font-size: 0.9rem;
    margin-top: 0.5rem;
    opacity: 0.9;
}

/* Estatísticas críticas */
.critical-stats {
    margin-bottom: 2rem;
}

.critical-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    border-left: 4px solid #e3e6f0;
    display: flex;
    align-items: center;
    gap: 1rem;
    transition: all 0.3s ease;
    margin-bottom: 1rem;
}

.critical-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.critical-card.high-priority {
    border-left-color: #dc3545;
}

.critical-card.medium-priority {
    border-left-color: #ffc107;
}

.critical-card.low-priority {
    border-left-color: #28a745;
}

.critical-card.resolution {
    border-left-color: #FE8D18;
}

.critical-icon {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: white;
}

.high-priority .critical-icon {
    background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%);
}

.medium-priority .critical-icon {
    background: linear-gradient(135deg, #ffc107 0%, #d39e00 100%);
}

.low-priority .critical-icon {
    background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);
}

.resolution .critical-icon {
    background: linear-gradient(135deg, #FE8D18 0%, #0056b3 100%);
}

.critical-value {
    font-size: 1.75rem;
    font-weight: 800;
    color: #2d3748;
    margin-bottom: 0.25rem;
}

.critical-label {
    font-size: 0.875rem;
    color: #718096;
    font-weight: 600;
}

/* Top problemas */
.top-problems {
    margin-bottom: 2rem;
}

.section-title {
    color: #2d3748;
    font-weight: 700;
    margin-bottom: 1rem;
    border-bottom: 2px solid #e3e6f0;
    padding-bottom: 0.5rem;
}

.problems-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.problem-item {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    border-left: 4px solid #e3e6f0;
    display: flex;
    align-items: center;
    gap: 1rem;
    transition: all 0.3s ease;
}

.problem-item:hover {
    transform: translateX(5px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.problem-item.critical-problem {
    border-left-color: #dc3545;
    background: linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%);
}

.problem-item.high-problem {
    border-left-color: #fd7e14;
    background: linear-gradient(135deg, #fff8f0 0%, #feebc8 100%);
}

.problem-item.medium-problem {
    border-left-color: #ffc107;
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
}

.problem-item.low-problem {
    border-left-color: #28a745;
    background: linear-gradient(135deg, #f0fff4 0%, #c6f6d5 100%);
}

.problem-rank {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1.25rem;
}

.problem-content {
    flex: 1;
}

.problem-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}

.problem-title {
    font-weight: 700;
    color: #2d3748;
    font-size: 1.1rem;
}

.severity-badge {
    padding: 0.25rem 0.75rem;
    border-radius: 15px;
    font-size: 0.75rem;
    font-weight: 700;
}

.severity-badge.critical {
    background: #dc3545;
    color: white;
}

.severity-badge.high {
    background: #fd7e14;
    color: white;
}

.severity-badge.medium {
    background: #ffc107;
    color: #212529;
}

.severity-badge.low {
    background: #28a745;
    color: white;
}

.problem-stats {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.problem-count {
    color: #718096;
    font-weight: 600;
}

.problem-percentage {
    font-weight: 700;
    color: #dc3545;
}

.problem-indicator {
    width: 100px;
}

.indicator-bar {
    height: 8px;
    background: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
}

.indicator-fill {
    height: 100%;
    background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%);
    transition: width 0.3s ease;
}

/* Container do gráfico */
.chart-container {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #e3e6f0;
}

.chart-header {
    margin-bottom: 1rem;
    border-bottom: 1px solid #e3e6f0;
    padding-bottom: 0.5rem;
}

.chart-title {
    color: #2d3748;
    font-weight: 700;
    margin-bottom: 0;
}

/* Tabela de problemas */
.problems-table {
    background: white;
    border-radius: 12px;
    overflow: hidden;
}

.table {
    margin-bottom: 0;
}

.table thead th {
    border-top: none;
    font-weight: 600;
    color: #5a5c69;
    background-color: #f8f9fc;
}

.table tbody tr:hover {
    background-color: #f8f9fc;
}

.table tbody tr.ok-row {
    background-color: #f8fff4;
}

.table tbody tr.ok-row:hover {
    background-color: #e8f5e8;
}

.problem-info .problem-name {
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 0.25rem;
}

.problem-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.impact-badge {
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
}

.impact-badge.high-impact {
    background: #f8d7da;
    color: #721c24;
}

.impact-badge.medium-impact {
    background: #fff3cd;
    color: #856404;
}

.impact-badge.low-impact {
    background: #d1ecf1;
    color: #0c5460;
}

.impact-badge.ok-status {
    background: #d4edda;
    color: #155724;
}

/* Estado vazio */
.empty-state {
    text-align: center;
    padding: 3rem;
}

.empty-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 0.5rem;
}

.empty-subtitle {
    color: #718096;
    font-size: 1.1rem;
}

/* Análise de tendências */
.trend-analysis {
    margin-top: 2rem;
}

.analysis-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #e3e6f0;
    height: 100%;
}

.analysis-title {
    color: #2d3748;
    font-weight: 700;
    margin-bottom: 1rem;
    border-bottom: 1px solid #e3e6f0;
    padding-bottom: 0.5rem;
}

.analysis-content {
    color: #4a5568;
}

.trend-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f1f3f4;
}

.trend-item:last-child {
    border-bottom: none;
}

.trend-label {
    font-weight: 600;
    color: #4a5568;
}

.trend-value {
    font-weight: 700;
    color: #2d3748;
}

.recommendation-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f1f3f4;
}

.recommendation-item:last-child {
    border-bottom: none;
}

.recommendation-icon {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    flex-shrink: 0;
}

.recommendation-text {
    flex: 1;
    font-weight: 500;
    color: #4a5568;
    line-height: 1.5;
}

/* Badges */
.badge {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
}

.badge-danger {
    background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%);
}

.badge-success {
    background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);
}

/* Seção de filtros */
.filters-section {
    background-color: #f8f9fa;
    padding: 20px;
    border-top: 1px solid #dee2e6;
}

.filters-section .form-group {
    margin-bottom: 1rem;
}

.filters-section label {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
}

/* Botão de ação do header */
.btn-action {
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    padding: 8px 16px;
    transition: all 0.2s ease;
}

.btn-action:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-action i {
    margin-right: 8px;
}

/* Ajustes para os botões de filtro */
.d-flex.gap-2 {
    gap: 0.5rem;
}

/* Formulários */
.form-label {
    font-weight: 600;
    color: #5a5c69;
    margin-bottom: 0.5rem;
}

.form-control {
    border-radius: 0.35rem;
    border: 1px solid #d1d3e2;
}

.form-control:focus {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

/* Responsividade */
@media (max-width: 768px) {
    .critical-card {
        flex-direction: column;
        text-align: center;
        gap: 1rem;
    }

    .critical-value {
        font-size: 1.5rem;
    }

    .problem-item {
        flex-direction: column;
        text-align: center;
        gap: 1rem;
    }

    .problem-header {
        flex-direction: column;
        gap: 0.5rem;
    }

    .problem-stats {
        flex-direction: column;
        gap: 0.5rem;
    }

    .trend-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.25rem;
    }

    .recommendation-item {
        flex-direction: column;
        text-align: center;
        gap: 0.5rem;
    }

    .table-responsive {
        font-size: 0.875rem;
    }

    .filters-section {
        padding: 15px;
    }

    .filters-section .row .col-md-3 {
        margin-bottom: 1rem;
    }

    .d-flex.gap-2 {
        flex-direction: column;
    }

    .d-flex.gap-2 .btn {
        width: 100%;
        margin-bottom: 0.5rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/Indicators/main_problems.vue